import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Building2, TrendingUp, TrendingDown, Calendar, CreditCard, Upload, ArrowUpRight, Briefcase, ArrowDown, ArrowUp, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtINR = (v) => v != null ? Math.abs(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const TYPE_CONFIG = {
  expense:    { label: 'Expense',    color: 'text-red-600',       bg: 'bg-red-50',       border: 'border-red-100',    icon: TrendingDown, dotColor: 'bg-red-500' },
  income:     { label: 'Income',     color: 'text-emerald-700',   bg: 'bg-emerald-50',   border: 'border-emerald-100',icon: TrendingUp,   dotColor: 'bg-emerald-500' },
  investment: { label: 'Investment', color: 'text-[#7C6B4F]',     bg: 'bg-amber-50',     border: 'border-amber-100',  icon: Building2,    dotColor: 'bg-amber-600' },
  transfer:   { label: 'Transfer',   color: 'text-blue-600',      bg: 'bg-blue-50',      border: 'border-blue-100',   icon: CreditCard,   dotColor: 'bg-blue-500' },
};

function CategorySummaryTable({ categories }) {
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortField, setSortField] = useState('total_debits');
  const [sortDir, setSortDir] = useState('desc');
  const [expanded, setExpanded] = useState(true);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const filtered = useMemo(() => {
    let list = categories;
    if (typeFilter !== 'all') list = list.filter(c => c.type === typeFilter);
    const dir = sortDir === 'desc' ? -1 : 1;
    return [...list].sort((a, b) => ((a[sortField] || 0) - (b[sortField] || 0)) * dir);
  }, [categories, typeFilter, sortField, sortDir]);

  const totals = useMemo(() => {
    return filtered.reduce((acc, c) => ({
      debits: acc.debits + c.total_debits,
      credits: acc.credits + c.total_credits,
      count: acc.count + c.count,
    }), { debits: 0, credits: 0, count: 0 });
  }, [filtered]);

  // Group counts by type
  const typeCounts = useMemo(() => {
    const map = {};
    categories.forEach(c => { map[c.type] = (map[c.type] || 0) + 1; });
    return map;
  }, [categories]);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowDown size={10} className="opacity-20" />;
    return sortDir === 'desc' ? <ArrowDown size={10} className="text-[#284A33]" /> : <ArrowUp size={10} className="text-[#284A33]" />;
  };

  return (
    <div className="bg-white border border-[#E2E0D8] rounded-md" data-testid="biz-category-summary">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 pb-3 border-b border-[#E2E0D8]">
        <div className="p-1.5 rounded-md bg-[#284A33]/10"><Briefcase size={14} className="text-[#284A33]" /></div>
        <div className="flex-1">
          <h3 className="font-heading text-sm font-semibold text-[#1C1C19]">Category Breakdown</h3>
          <p className="text-[10px] text-[#5C5C55] mt-0.5">{categories.length} categories · {categories.reduce((s, c) => s + c.count, 0)} entries</p>
        </div>
        <button onClick={() => setExpanded(e => !e)} className="p-1 rounded hover:bg-[#F7F6F2] transition-colors">
          {expanded ? <ChevronUp size={16} className="text-[#5C5C55]" /> : <ChevronDown size={16} className="text-[#5C5C55]" />}
        </button>
      </div>

      {expanded && (
        <>
          {/* Type filter pills */}
          <div className="flex items-center gap-1.5 px-4 py-3 border-b border-[#E2E0D8]/60" data-testid="biz-cat-type-filters">
            <button onClick={() => setTypeFilter('all')} data-testid="biz-cat-filter-all"
              className={`px-3 py-1 text-[11px] font-medium rounded-full transition-colors ${typeFilter === 'all' ? 'bg-[#1C1C19] text-white' : 'text-[#5C5C55] hover:bg-[#E8E6DE] border border-[#E2E0D8]'}`}>
              All ({categories.length})
            </button>
            {Object.entries(TYPE_CONFIG).map(([key, cfg]) => {
              const count = typeCounts[key] || 0;
              if (!count) return null;
              return (
                <button key={key} onClick={() => setTypeFilter(key)} data-testid={`biz-cat-filter-${key}`}
                  className={`px-3 py-1 text-[11px] font-medium rounded-full transition-colors ${
                    typeFilter === key ? `${cfg.bg} ${cfg.color} border ${cfg.border}` : 'text-[#5C5C55] hover:bg-[#E8E6DE] border border-[#E2E0D8]'
                  }`}>
                  {cfg.label} ({count})
                </button>
              );
            })}
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="biz-category-table">
              <thead>
                <tr className="bg-[#F7F6F2]/60">
                  <th className="text-left p-3 pl-4 text-[10px] uppercase tracking-wider text-[#5C5C55] font-medium">Category</th>
                  <th className="text-center p-3 text-[10px] uppercase tracking-wider text-[#5C5C55] font-medium">Type</th>
                  <th className="text-center p-3 text-[10px] uppercase tracking-wider text-[#5C5C55] font-medium">
                    <button onClick={() => toggleSort('count')} className="inline-flex items-center gap-1 hover:text-[#1C1C19] transition-colors">
                      Entries <SortIcon field="count" />
                    </button>
                  </th>
                  <th className="text-right p-3 text-[10px] uppercase tracking-wider text-[#5C5C55] font-medium">
                    <button onClick={() => toggleSort('total_debits')} className="inline-flex items-center gap-1 hover:text-[#1C1C19] transition-colors">
                      Debits <SortIcon field="total_debits" />
                    </button>
                  </th>
                  <th className="text-right p-3 pr-4 text-[10px] uppercase tracking-wider text-[#5C5C55] font-medium">
                    <button onClick={() => toggleSort('total_credits')} className="inline-flex items-center gap-1 hover:text-[#1C1C19] transition-colors">
                      Credits <SortIcon field="total_credits" />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const cfg = TYPE_CONFIG[c.type] || TYPE_CONFIG.expense;
                  return (
                    <tr key={c.name} className="border-t border-[#E2E0D8]/40 hover:bg-[#F7F6F2]/40 transition-colors" data-testid={`biz-cat-row-${c.name.replace(/\s+/g, '-')}`}>
                      <td className="p-3 pl-4">
                        <span className="text-sm font-medium text-[#1C1C19]">{c.name}</span>
                      </td>
                      <td className="p-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${cfg.bg} ${cfg.color} border ${cfg.border}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${cfg.dotColor}`} />
                          {cfg.label}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <span className="text-xs font-mono text-[#5C5C55]">{c.count}</span>
                      </td>
                      <td className="p-3 text-right">
                        {c.total_debits > 0 ? (
                          <span className="text-sm font-mono text-red-600">₹{fmtINR(c.total_debits)}</span>
                        ) : (
                          <span className="text-sm font-mono text-[#D5D2C6]">—</span>
                        )}
                      </td>
                      <td className="p-3 pr-4 text-right">
                        {c.total_credits > 0 ? (
                          <span className="text-sm font-mono text-emerald-700">₹{fmtINR(c.total_credits)}</span>
                        ) : (
                          <span className="text-sm font-mono text-[#D5D2C6]">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {/* Totals footer */}
              <tfoot>
                <tr className="border-t-2 border-[#1C1C19]/15 bg-[#F7F6F2]/80">
                  <td className="p-3 pl-4"><span className="text-sm font-semibold text-[#1C1C19]">Total ({filtered.length} categories)</span></td>
                  <td></td>
                  <td className="p-3 text-center"><span className="text-xs font-mono font-semibold text-[#1C1C19]">{totals.count}</span></td>
                  <td className="p-3 text-right"><span className="text-sm font-mono font-semibold text-red-600">₹{fmtINR(totals.debits)}</span></td>
                  <td className="p-3 pr-4 text-right"><span className="text-sm font-mono font-semibold text-emerald-700">₹{fmtINR(totals.credits)}</span></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default function BusinessDashboardPage() {
  const [data, setData] = useState(null);
  const [fy, setFy] = useState('all');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (fy !== 'all') params.fy = fy;
      const { data: d } = await axios.get(`${API}/business/dashboard`, { params, withCredentials: true });
      setData(d);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [fy]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const hasData = data?.summary?.count > 0;
  const summary = data?.summary || {};
  const categories = data?.categories || [];
  const trends = (data?.trends || []).map(t => {
    const [y, m] = t.month.split('-');
    return { month: `${MONTHS[parseInt(m) - 1]}-${y.slice(2)}`, debits: t.debits, credits: t.credits };
  });
  const availableFYs = data?.available_fys || [];

  return (
    <div className="p-6 lg:p-8 space-y-5 max-w-[1400px]" data-testid="business-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-[#1C1C19]">Business Dashboard</h2>
          <p className="text-sm text-[#5C5C55] mt-1">ICICI Business Account overview</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={fy} onValueChange={setFy}>
            <SelectTrigger className="w-[140px] h-8 text-xs border-[#E2E0D8]" data-testid="biz-dash-fy-filter">
              <Calendar size={12} className="mr-1.5 text-[#5C5C55]" />
              <SelectValue placeholder="All FYs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              {availableFYs.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>
          {hasData && (
            <button onClick={() => navigate('/business/upload')} data-testid="biz-dash-upload-btn"
              className="flex items-center gap-2 bg-[#284A33] text-white hover:bg-[#1C3624] px-4 py-1.5 rounded-md text-xs font-medium transition-colors">
              <Upload size={13} /> Upload
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#5C5C55] text-sm">Loading...</div>
      ) : !hasData ? (
        <div className="bg-white border border-[#E2E0D8] rounded-md p-16 text-center">
          <Building2 className="mx-auto mb-4 text-[#D5D2C6]" size={48} />
          <h3 className="font-heading text-xl text-[#1C1C19] mb-2">No Business Data Yet</h3>
          <p className="text-[#5C5C55] mb-6 text-sm">Upload business bank or credit card statements to see your dashboard</p>
          <button onClick={() => navigate('/business/upload')} data-testid="biz-dash-upload-link"
            className="inline-flex items-center gap-2 bg-[#284A33] text-white hover:bg-[#1C3624] px-6 py-2.5 rounded-md font-medium transition-colors text-sm">
            Upload Statement <ArrowUpRight size={15} />
          </button>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white border border-[#E2E0D8] rounded-lg p-4" data-testid="biz-dash-count">
              <p className="text-[10px] uppercase tracking-wider text-[#5C5C55] font-medium">Total Entries</p>
              <p className="text-xl font-heading font-bold text-[#1C1C19] mt-1">{summary.count}</p>
            </div>
            <div className="bg-white border border-[#E2E0D8] rounded-lg p-4" data-testid="biz-dash-debits">
              <p className="text-[10px] uppercase tracking-wider text-[#5C5C55] font-medium">Total Debits</p>
              <p className="text-xl font-heading font-bold text-red-600 mt-1">₹{fmtINR(summary.total_debits)}</p>
            </div>
            <div className="bg-white border border-[#E2E0D8] rounded-lg p-4" data-testid="biz-dash-credits">
              <p className="text-[10px] uppercase tracking-wider text-[#5C5C55] font-medium">Total Credits</p>
              <p className="text-xl font-heading font-bold text-emerald-700 mt-1">₹{fmtINR(summary.total_credits)}</p>
            </div>
            <div className="bg-white border border-[#E2E0D8] rounded-lg p-4" data-testid="biz-dash-untagged">
              <p className="text-[10px] uppercase tracking-wider text-[#5C5C55] font-medium">Untagged</p>
              <p className="text-xl font-heading font-bold text-orange-500 mt-1">{summary.untagged}</p>
            </div>
          </div>

          {/* Category Breakdown Table */}
          {categories.length > 0 && <CategorySummaryTable categories={categories} />}

          {/* Monthly Trends */}
          {trends.length > 0 && (
            <div className="bg-white border border-[#E2E0D8] rounded-md p-5" data-testid="biz-trends-chart">
              <h3 className="font-heading text-sm font-semibold text-[#1C1C19] mb-4">Monthly Trends</h3>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E0D8" />
                  <XAxis dataKey="month" tick={{ fill: '#5C5C55', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#5C5C55', fontSize: 11 }} tickFormatter={(v) => `₹${fmtINR(v)}`} />
                  <Tooltip formatter={(v) => `₹${fmtINR(v)}`} contentStyle={{ borderRadius: '6px', border: '1px solid #E2E0D8', fontSize: '12px' }} />
                  <Area type="monotone" dataKey="debits" stroke="#DC2626" fill="#DC2626" fillOpacity={0.08} strokeWidth={2} name="Debits" />
                  <Area type="monotone" dataKey="credits" stroke="#059669" fill="#059669" fillOpacity={0.08} strokeWidth={2} name="Credits" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </div>
  );
}
